/**
 * useRecords Composable
 *
 * Generic records-list composable. Owns a reactive list of records fetched
 * by a caller-supplied query, plus the field metadata, mutation helpers, and
 * optimistic-update plumbing that go with it. Re-fetches when variables change.
 *
 * Links and backlinks are not fetched here. If a view needs them attached to
 * each record, use useLinkedRecords (which composes useRecords + useLinks).
 *
 * @example
 * const records = useRecords({
 *   query: fetchRecordsByType,
 *   variables: computed(() => ({ type: props.recordType })),
 * }, deps)
 */

import { computed, type MaybeRefOrGetter, ref, shallowRef, toValue, triggerRef, watch } from "vue"
import type { OperationVariables } from "@apollo/client/core"
import type { TypedDocumentNode } from "@graphql-typed-document-node/core"
import { lockError } from "@auger/core"
import {
  applyRelationChanges,
  computeRelationOps,
  RECORD_FIXED_KEYS,
} from "@auger/core/collections"
import { isReferenceField, isRelationField } from "@auger/core/fields"
import type { RecordFieldsFragment as RecordRow } from "@auger/core/gql/types"
import { readOnlyWhenLocked } from "@/common/fields/locks"
import { getAuger } from "@/init/auger"
import { errorMessage } from "@/utils/errors"
import type { ComposableDeps } from "./types"
import { useFields } from "./useFields"
import { arbitrate } from "./writeArbiter"

// Minimum contract every records-query must satisfy: returns a `records` array
// of objects with at least `id` and an optional jsonb `data` blob.
type RecordsLike = { id: number; data?: Record<string, unknown> | null }
type RecordsResult = { records: ReadonlyArray<RecordsLike> }
// A document is contravariant in its variables, so `never` is the variables type that
// accepts a document whatever variables it declares. The query call names its own types.
export type AnyRecordsDoc = TypedDocumentNode<RecordsResult, never>

// Extract the item type out of a records-query document so callers get the
// exact record shape their query selected (taggings, links, etc. typed).
// Falls back to RecordRow rather than `never` when inference fails (e.g.
// when called from a generic wrapper that doesn't forward the document type),
// so consumers degrade to the canonical fragment shape instead of `never`.
export type RecordItemOf<TDoc> =
  TDoc extends TypedDocumentNode<infer R, never>
    ? R extends { records: ReadonlyArray<infer Item> }
      ? Item
      : RecordRow
    : RecordRow

export interface UseRecordsOptions<TDoc extends AnyRecordsDoc> {
  query: TDoc
  variables: MaybeRefOrGetter<Record<string, unknown>>
}

/**
 * Composable for managing a reactive list of records.
 *
 * Variables may be a plain object, ref, or computed; a change to the resolved
 * value triggers a refetch. A `type` key in variables, when present, selects
 * type-specific schemas for the derived `fields` list.
 */
export function useRecords<TDoc extends AnyRecordsDoc>(
  options: UseRecordsOptions<TDoc>,
  deps: ComposableDeps
) {
  type Item = RecordItemOf<TDoc>

  const { client, toast } = deps
  const { query, variables: variablesOption } = options

  if (!query || !variablesOption) {
    throw new Error("useRecords: query & variables options are required")
  }

  // Monotonic counter for stale-result guards. If variables change mid-flight,
  // the in-flight token becomes stale and the late-arriving result is dropped.
  let fetchToken = 0

  const getVariables = () => toValue(variablesOption)

  // --- State ---
  // shallowRef: items is replaced wholesale, never deeply mutated. Avoids the
  // cost of Vue deep-reactivity on large record sets.
  const items = shallowRef<Item[]>([])

  const typeName = computed(() => {
    const type = getVariables().type
    return typeof type === "string" ? type : null
  })
  const loading = ref(false)
  const error = ref<Error | null>(null)

  // --- Computed ---

  // The fields of the loaded records: fixed columns + type-schema fields + the
  // superset of custom-schema fields available for this type + the on-record
  // relationship fields (tags, contacts, times, attachments). Each field carries
  // its own provenance (schema_id, subtype) for consumers that want to group or
  // filter by source.
  // The types and schemas the loaded records use: every record's type, and its own
  // schema. The fields offered are those of these schemas (see useFields); null
  // until records load, when every schema of the type is offered.
  const typesInResult = computed(() => [
    ...new Set(items.value.map((r) => (r as { type?: string }).type).filter((t) => !!t)),
  ])
  const schemaIdsInResult = computed<number[] | null>(() =>
    items.value.length
      ? [
          ...new Set(
            items.value
              .map((r) => (r as { schema_id?: number | null }).schema_id)
              .filter((id): id is number => id != null)
          ),
        ]
      : null
  )

  const {
    recordType,
    fields: typeFields,
    schemaNames,
    typeSchemaFields,
    recordSchemaFields,
  } = useFields(typeName, { schemaIds: schemaIdsInResult })
  // A locked record's row reads only: each item is the record itself.
  const fields = computed(() => typeFields.value.map((f) => readOnlyWhenLocked(f, (item) => item)))

  // --- Data Fetching ---

  // Fetch records. Items appear as soon as this resolves. Stale results from
  // prior variables are dropped via fetchToken.
  function fetchRecords() {
    const variables = getVariables()
    const startToken = ++fetchToken

    loading.value = true
    error.value = null

    return client
      .query<RecordsResult, OperationVariables>({ query, variables, fetchPolicy: "no-cache" })
      .then(({ data }) => {
        if (startToken !== fetchToken) return items.value
        items.value = [...(data?.records ?? [])] as Item[]
        return items.value
      })
      .catch((err) => {
        const message = errorMessage(err)
        if (startToken !== fetchToken) return
        error.value = err
        const label = typeName.value ? `${typeName.value}s` : "records"
        toast?.error?.(`Failed to load ${label}: ${message}`)
        throw err
      })
      .finally(() => {
        if (startToken === fetchToken) loading.value = false
      })
  }

  // Refresh a single record by id; replaces it in the items array.
  function refreshItem(id: number) {
    return getAuger(client)
      .records.get(id)
      .then((record) => {
        if (!record) return null

        // The caller's query may select keys the full record lacks; merge so
        // those survive the refresh.
        const index = items.value.findIndex((r) => r.id === id)
        const current = items.value[index]
        if (current) {
          items.value[index] = { ...current, ...record }
          triggerRef(items)
        }
        return record
      })
      .catch((err: Error): null => {
        console.error("Failed to refresh record:", err)
        return null
      })
  }

  // Fetch now, and again whenever the variables change.
  watch(
    () => JSON.stringify(getVariables()),
    () => fetchRecords(),
    { immediate: true }
  )

  // --- Record Mutations ---

  // Update a single field on a record with optimistic update.
  //
  // `async` is deliberate and changes a failure path: the write arbiters below
  // throw synchronously, and before this was async such a throw propagated out
  // of the Vue event handler into app.config.errorHandler. Now it is a rejected
  // promise, so callers must consume it — and the arbiter calls are wrapped in
  // `arbitrate` so the refusal toasts even when a caller does not.
  async function updateItem(id: number, fieldKey: string, value: unknown) {
    const index = items.value.findIndex((r) => r.id === id)
    const record = items.value[index]
    if (!record) {
      toast?.error?.("Record not found")
      return false
    }

    const field = fields.value.find((f) => f.key === fieldKey)

    // Relation fields: reconcile the field's schema_key-scoped rows via one bulk
    // add + one bulk remove (record.data untouched). The value is the field's
    // whole set, or an `{ add, remove, update }` delta (a contact or time cell's draft). Patch the record's rows
    // optimistically so the chips update on commit; the refetch converges (temp
    // ids → real rows), and a failure rolls back before converging.
    if (field && isRelationField(field)) {
      const plan = arbitrate("record", toast, () => computeRelationOps(field, record, value))
      items.value[index] = arbitrate("record", toast, () =>
        applyRelationChanges(field, record, plan)
      )
      triggerRef(items)
      try {
        await getAuger(client).records.update(
          record.id,
          { [field.sourceKey ?? field.key]: value },
          { read: false }
        )
        const refreshed = await refreshItem(id)
        if (refreshed === null) {
          // The writes landed but the refetch came back empty — the record
          // vanished or raced a delete. `false` reads as "write failed," which
          // it didn't; warn so it's diagnosable rather than a silent false.
          console.warn(
            `updateItem: relation sync succeeded for record ${id} but refresh found nothing`
          )
        }
        return refreshed !== null
      } catch (err) {
        const error = errorMessage(err)
        items.value[index] = record
        triggerRef(items)
        console.error(err)
        toast?.error?.(`Failed to update: ${error}`)
        // Creates may have landed before the failure; converge with the server
        // so the next edit diffs against real rows.
        await refreshItem(id).catch(() => undefined)
        return false
      }
    }

    // Store/column reference: the committed value is the target ENTITY; persist
    // its id (or ids). Value fields store their value.
    let stored = value
    if (field && isReferenceField(field)) {
      const ids = (value == null ? [] : Array.isArray(value) ? value : [value])
        .filter((e) => e != null)
        .map((e) => (e && typeof e === "object" && "id" in e ? (e as { id: unknown }).id : e))
      stored = field.isArray ? ids : (ids[0] ?? null)
    }

    return updateItemFields(id, { [field?.sourceKey ?? fieldKey]: stored })
  }

  // Write several value/column keys on one record as a single optimistic
  // mutation — the collection counterpart of useRecord's `update(changes)`.
  // One write, not a run of updateItem calls: each would build its `_set` from
  // the same `data`, so concurrent calls race and a later one can drop an
  // earlier one's key. Keys are stored values (ids, not entities); relation
  // fields don't belong here — they go through updateItem.
  function updateItemFields(id: number, changes: Record<string, unknown>) {
    const index = items.value.findIndex((r) => r.id === id)
    const record = items.value[index]
    if (!record) {
      toast?.error?.("Record not found")
      return Promise.resolve(false)
    }

    // Optimistic update: direct record columns at the top level, the rest in data
    const data: Record<string, unknown> = { ...(record.data || {}) }
    const optimistic: Record<string, unknown> = { ...record, data }
    for (const [key, value] of Object.entries(changes)) {
      if (RECORD_FIXED_KEYS.includes(key)) optimistic[key] = value
      else data[key] = value
    }
    items.value[index] = optimistic as Item
    triggerRef(items)

    return getAuger(client)
      .records.update(record.id, changes, { read: false })
      .then(({ record: updated }) => {
        items.value[index] = { ...items.value[index], ...updated } as Item
        triggerRef(items)
        // Refresh the full record to get updated relationships (schema, etc.)
        return refreshItem(id)
      })
      .catch((err) => {
        const error = errorMessage(err)
        items.value[index] = record
        triggerRef(items)
        toast?.error?.(`Failed to update: ${error}`)
        return false
      })
  }

  // Write several records' fields as one gesture. Core has no single write that
  // gives each record its own values, so this is one concurrent `records.update`
  // per record, each record's keys merged into its call (see updateItemFields);
  // identical changes to every record collapse into one `records.updateMany`. A gesture known to
  // fail is refused before anything is painted or written; a record that fails
  // afterwards is put back, and the failures share one toast.
  async function updateItems(
    writes: Array<{ itemKey: number | string; fields: Record<string, unknown> }>
  ): Promise<{ written: number; failed: Array<{ itemKey: string; reason: string }> }> {
    interface Plan {
      id: number
      index: number
      before: Item
      after: Item
      changes: Record<string, unknown>
      relation: boolean
    }
    const plans: Plan[] = []
    const refuse = (message: string) => {
      toast?.error?.(message)
      return { written: 0, failed: [] }
    }

    try {
      for (const { itemKey, fields: values } of writes) {
        const id = Number(itemKey)
        const index = items.value.findIndex((r) => r.id === id)
        const before = items.value[index]
        if (!before) return refuse("A record is no longer in the list.")
        let after: Item = before
        const changes: Record<string, unknown> = {}
        let relation = false
        for (const [fieldKey, value] of Object.entries(values)) {
          const field = fields.value.find((f) => f.key === fieldKey)
          if (!field) return refuse(`Unknown field: ${fieldKey}`)
          if (isRelationField(field)) {
            const current = after
            const plan = arbitrate("record", toast, () => computeRelationOps(field, current, value))
            after = arbitrate("record", toast, () => applyRelationChanges(field, current, plan))
            changes[field.sourceKey ?? field.key] = value
            relation = true
            continue
          }
          let stored = value
          if (isReferenceField(field)) {
            const ids = (value == null ? [] : Array.isArray(value) ? value : [value])
              .filter((e) => e != null)
              .map((e) => (e && typeof e === "object" && "id" in e ? (e as { id: unknown }).id : e))
            stored = field.isArray ? ids : (ids[0] ?? null)
          }
          const dataKey = field.sourceKey ?? fieldKey
          after = RECORD_FIXED_KEYS.includes(dataKey)
            ? ({ ...after, [dataKey]: stored } as Item)
            : ({ ...after, data: { ...(after.data || {}), [dataKey]: stored } } as Item)
          changes[dataKey] = stored
        }
        plans.push({ id, index, before, after, changes, relation })
      }
    } catch {
      // arbitrate toasted its own refusals.
      return { written: 0, failed: [] }
    }
    if (plans.length === 0) return { written: 0, failed: [] }

    for (const p of plans) items.value[p.index] = p.after
    triggerRef(items)

    // Every record getting the same non-relation changes (a fill, a single pasted
    // value, a bulk edit) is one `records.updateMany`; anything else is one
    // `records.update` per record.
    const auger = getAuger(client)
    const first = JSON.stringify(plans[0]!.changes)
    const uniform =
      plans.length > 1 && plans.every((p) => !p.relation && JSON.stringify(p.changes) === first)
    const reasons = new Map<number, unknown>()
    if (uniform) {
      try {
        const { missingIds } = await auger.records.updateMany(
          plans.map((p) => p.id),
          plans[0]!.changes
        )
        for (const id of missingIds) reasons.set(id, "No longer in the list.")
      } catch (err) {
        for (const p of plans) reasons.set(p.id, err)
      }
    } else {
      const outcomes = await Promise.allSettled(
        plans.map((p) => auger.records.update(p.id, p.changes, { read: false }))
      )
      outcomes.forEach((outcome, i) => {
        if (outcome.status === "rejected") reasons.set(plans[i]!.id, outcome.reason)
      })
    }
    const failed: Array<{ itemKey: string; reason: string }> = []
    for (const p of plans) {
      if (!reasons.has(p.id)) continue
      const reason = reasons.get(p.id)
      console.error(reason)
      failed.push({ itemKey: String(p.id), reason: errorMessage(reason) })
      items.value[p.index] = p.before
    }
    triggerRef(items)

    if (failed.length) {
      toast?.error?.(`${failed.length} of ${plans.length} not written: ${failed[0]!.reason}`)
    }
    // Converge a relation's rows on the server's, written or not (a failed write
    // may have landed partway).
    await Promise.all(
      plans.filter((p) => p.relation).map((p) => refreshItem(p.id).catch(() => undefined))
    )
    return { written: plans.length - failed.length, failed }
  }

  // Archive/unarchive a single record. Deliberately bypasses updateItem's
  // fixed/data-field partition — archived_at is not in RECORD_FIXED_KEYS by
  // design, so it must go through the dedicated service call, not a generic
  // field write that would nest it under `data`.
  function setArchived(id: number, archive: boolean) {
    const index = items.value.findIndex((r) => r.id === id)
    const record = items.value[index]
    if (!record) {
      toast?.error?.("Record not found")
      return Promise.resolve(false)
    }

    items.value[index] = { ...record, archived_at: archive ? new Date().toISOString() : null }
    triggerRef(items)

    const { records } = getAuger(client)
    return (archive ? records.archive(id) : records.unarchive(id))
      .then((updated) => {
        items.value[index] = { ...items.value[index]!, archived_at: updated.archived_at }
        triggerRef(items)
        return true
      })
      .catch((err) => {
        const error = errorMessage(err)
        items.value[index] = record
        triggerRef(items)
        toast?.error?.(`Failed to ${archive ? "archive" : "unarchive"}: ${error}`)
        return false
      })
  }

  const archiveItem = (id: number) => setArchived(id, true)
  const unarchiveItem = (id: number) => setArchived(id, false)

  // Destroy a single record (optimistic; rolls back on error). Irreversible —
  // unlike archive/unarchive there is no server-side undo.
  function destroyItem(id: number) {
    const index = items.value.findIndex((r) => r.id === id)
    if (index === -1) {
      toast?.error?.("Record not found")
      return Promise.resolve(false)
    }

    const record = items.value[index] as Item & { name?: string }
    const previousItems = [...items.value]

    items.value = items.value.filter((r) => r.id !== id)

    // destroy_record is site_admin-only in Hasura; every session defaults to `user`.
    return getAuger(client, { role: "admin" })
      .records.destroy(id)
      .then(() => {
        toast?.success?.(`Destroyed ${record.name ?? "record"}`)
        return true
      })
      .catch((err: unknown) => {
        items.value = previousItems
        const error = errorMessage(err)
        console.error(`destroyItem(${id}) failed`, err)
        toast?.error?.(`Failed to destroy: ${error}`)
        return false
      })
  }

  // Destroy multiple records (optimistic; rolls back on error). On partial
  // failure, only the records that actually failed are restored to
  // items.value — the ones that were genuinely destroyed stay removed.
  function destroyItems(ids: number[]) {
    if (!ids.length) return Promise.resolve(false)

    const previousItems = [...items.value]

    items.value = items.value.filter((r) => !ids.includes(r.id))

    // Each id is its own destroy, so the result can be partial: a missing id or
    // a failed destroy comes back by id, beside the ones that went.
    return getAuger(client, { role: "admin" })
      .records.destroyMany(ids)
      .then(({ destroyedIds, missingIds, failures: failed }) => {
        const destroyed = destroyedIds.length
        const failures = [
          ...missingIds.map((id) => ({ id, reason: `Record ${id} not found for delete` })),
          ...failed.map(({ id, message }) => ({ id, reason: message })),
        ]
        if (failures.length) {
          const failedIds = new Set(failures.map((f) => f.id))
          const restored = previousItems.filter((r) => failedIds.has(r.id))
          items.value = [...items.value, ...restored]
          console.error("destroyItems: some ids failed to destroy", failures)
          const lockedCount = failed.filter((f) => lockError({ message: f.message })).length
          const why = lockedCount ? ` (${lockedCount} locked or linked from a locked record)` : ""
          toast?.error?.(
            destroyed > 0
              ? `Destroyed ${destroyed} of ${ids.length} records — the rest could not be destroyed${why}`
              : `Records could not be destroyed${why}`
          )
          return destroyed > 0
        }
        toast?.success?.(`Destroyed ${ids.length} records`)
        return true
      })
      .catch((err: unknown) => {
        items.value = previousItems
        const error = errorMessage(err)
        console.error("destroyItems failed", err)
        toast?.error?.(`Failed to destroy records: ${error}`)
        return false
      })
  }

  return {
    // Data
    items,

    // State
    loading,
    error,

    // Computed
    recordType,
    fields,
    typesInResult,
    schemaIdsInResult,
    schemaNames,
    typeSchemaFields,
    recordSchemaFields,

    // Methods
    refresh: fetchRecords,
    refreshItem,
    fetchRecords,
    updateItem,
    updateItemFields,
    updateItems,
    archiveItem,
    unarchiveItem,
    destroyItem,
    destroyItems,
  }
}
